import { useEffect, useCallback, useRef } from 'react';

const API_URL = '';

/**
 * currentApp -> tracked page name, for PUBLIC pages only.
 *
 * Tracking used to be opt-in per component, so only 4 of ~20 routes were
 * ever counted and every page added since was silently invisible in Admin
 * statistics. This map is the single source of truth instead: add a route
 * here and it is tracked.
 *
 * Logged-in app surfaces (notebook, gym, walking, admin) are deliberately
 * ABSENT — owner decision: statistics should measure the public/SEO
 * surface, not in-app screen changes.
 *
 * Individual exercise / trainer / blog pages are GROUPED (there are ~900
 * exercise pages; one row each would make the page list unreadable).
 */
export const PUBLIC_PAGE_NAMES: Record<string, string> = {
  landing: '/',
  calculators: '/calculators',
  'muscular-potential': '/muscular-potential-calculator',
  fatai: '/fatai',
  blog: '/blog',
  'blog-post': '/blog/*',
  exercises: '/exercises',
  'exercise-page': '/exercises/*',
  trainers: '/trainers',
  'trainer-page': '/trainers/*',
  'hall-of-fame': '/hall-of-fame',
  'workout-subpage': '/workout',
  'nutrition-subpage': '/nutrition',
  'ai-planner-subpage': '/ai-planner-and-analyzer',
  privacy: '/privacy',
  'medical-disclaimer': '/medical-disclaimer',
};

/** Resolve the tracked name, or null when the page should not be tracked. */
export function resolvePublicPageName(currentApp: string): string | null {
  // `currentApp === 'exercises'` serves both the index and the per-muscle SEO
  // landing pages. Those are a separate SEO surface, so they get their own row.
  if (
    currentApp === 'exercises' &&
    typeof window !== 'undefined' &&
    window.location.pathname.startsWith('/exercises/by-muscle/')
  ) {
    return '/exercises/by-muscle/*';
  }
  return PUBLIC_PAGE_NAMES[currentApp] ?? null;
}

// Generate or retrieve session ID
const getSessionId = (): string => {
  let sessionId = sessionStorage.getItem('analytics_session_id');
  if (!sessionId) {
    sessionId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('analytics_session_id', sessionId);
  }
  return sessionId;
};

/**
 * Hook to track page views and analytics.
 * Pass `null` to skip tracking (non-public page).
 */
export function usePageView(pageName: string | null): void {
  // Tracks the LAST page name sent rather than a boolean. The previous
  // boolean ref was never reset, so in this SPA only the first page a
  // visitor landed on was ever recorded — navigating on to another page
  // sent nothing.
  const lastSent = useRef<string | null>(null);

  useEffect(() => {
    if (!pageName || lastSent.current === pageName) return;
    lastSent.current = pageName;

    const trackPageView = async () => {
      try {
        await fetch(`${API_URL}/api/analytics/track`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            page: pageName,
            event_type: 'pageview',
            session_id: getSessionId(),
            referrer: document.referrer || null,
            user_agent: navigator.userAgent,
            screen_width: window.screen.width,
            screen_height: window.screen.height,
            timestamp: new Date().toISOString()
          })
        });
      } catch (error) {
        console.debug('Analytics tracking failed:', error);
      }
    };

    trackPageView();
  }, [pageName]);
}

/** Track the current public page, driven by App's `currentApp` router state. */
export function usePublicPageView(currentApp: string): void {
  usePageView(resolvePublicPageName(currentApp));
}

type TrackEventFunction = (eventType: string, page: string, metadata?: Record<string, any>) => Promise<void>;

/**
 * Hook to get tracking function for custom events
 */
export function useTrackEvent(): TrackEventFunction {
  const trackEvent = useCallback(async (
    eventType: string,
    page: string,
    metadata: Record<string, any> = {}
  ) => {
    try {
      await fetch(`${API_URL}/api/analytics/track`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          page,
          event_type: eventType,
          session_id: getSessionId(),
          referrer: document.referrer || null,
          user_agent: navigator.userAgent,
          screen_width: window.screen.width,
          screen_height: window.screen.height,
          timestamp: new Date().toISOString(),
          ...metadata
        })
      });
    } catch (error) {
      console.debug('Analytics event tracking failed:', error);
    }
  }, []);
  
  return trackEvent;
}

export default usePageView;
